/**
 * Live visualisation-methodology caption (issue #178): states, for the
 * camera's current framing, the terminology a planning-visualisation review
 * expects — focal-length equivalent, the named Landscape Institute TGN 06/19
 * convention when True scale is active, the viewing-distance note, eye
 * height, and a standing "illustrative, not survey-grade" caveat. This is
 * the caption that also carries the official-sheet caveat on screen: a 90°
 * developer wireline panorama is context only, never for judging scale.
 *
 * Pure compute/format module (mirrors ui/coordinateReadout.ts's split);
 * main.ts wires it to the DOM and refreshes it on preset/FOV/eye-height
 * change only — never per frame (repo performance rule). Presentation-only:
 * never read by any LOS result or code path (hard constraint #1), and this
 * module has no three.js/DOM dependency so it is unit-tested in plain Node.
 *
 * `text` is rendered as ONE element that WRAPS rather than a CSS ellipsis
 * (post-review fix): the original defect was a single string with the
 * caveat at the END, truncated by a CSS ellipsis on a narrow viewport — the
 * caveat itself (the whole reason this feature exists) never reached the
 * reader. `guaranteed` — the preset's scale claim plus the standing caveat —
 * always LEADS `text` and is kept short by construction (bounded, no
 * data-derived content), so even a hard visual limit eats into the trailing
 * focal-length/eye-height detail first, and a couple of wrapped lines never
 * grows large enough to collide with the angular scale bar's fixed
 * clearance above `#hud-bottom` (issue #177, --bottom-stack-offset + 210px).
 */
import { focalLengthMmEquivalent, type ViewScalePreset } from '../camera/viewScale';

export interface MethodologyCaptionState {
  preset: ViewScalePreset;
  /** Live camera vertical FOV, degrees. */
  fovDeg: number;
  /** The eye height disclosed alongside this caption — the active
   * viewpoint's own `eye_height_m`, or the documented 1.6 m default. */
  eyeHeightM: number;
  /** True when `eyeHeightM` is the 1.6 m default rather than a viewpoint's
   * own stated value (hard constraint #7 — a default must never be shown as
   * though it were a surveyed/authored figure). */
  eyeHeightIsDefault: boolean;
}

export interface MethodologyCaption {
  /** The full caption to render, in one normally-wrapping element. Always
   * starts with `guaranteed`. */
  text: string;
  /** The preset's scale claim (or the neutral absence of one) plus the
   * standing illustrative/not-survey-grade caveat — the wording this feature
   * exists to put in front of a reader, asserted independently so a
   * regression that drops or reorders it cannot hide behind `text` still
   * "containing" the words somewhere. */
  guaranteed: string;
}

/** Standing caveat (issue #178 AC): present for every preset, never
 * abbreviated away — the render is a flat local-grid projection, not a
 * survey-grade montage, whichever framing is active. */
const ILLUSTRATIVE_CAVEAT = 'illustrative planar projection, not survey-grade';

function eyeHeightSegment(state: MethodologyCaptionState): string {
  const value = `eye ${state.eyeHeightM.toFixed(1)} m`;
  return state.eyeHeightIsDefault ? `${value} (default)` : value;
}

/**
 * Formats the caption for the current preset/FOV. True scale and Official
 * sheet each name the specific claim they are — or are not — entitled to
 * make. Every other preset (Explore, or a custom `fov_deg` carried in by a
 * viewpoint) gets neutral focal-length/FOV wording with no true-scale or
 * context-panorama claim attached — the explicit failure case issue #178
 * calls out for anything that isn't a named scale convention. The standing
 * caveat is always part of `guaranteed`, regardless of preset.
 */
export function formatMethodologyCaption(state: MethodologyCaptionState): MethodologyCaption {
  const mm = focalLengthMmEquivalent(state.fovDeg);
  const eye = eyeHeightSegment(state);
  const detail = `${mm}mm equiv · ${eye}`;
  let guaranteed: string;
  if (state.preset === 'true-scale') {
    guaranteed = `LI TGN 06/19 · view at arm's length for true scale · ${ILLUSTRATIVE_CAVEAT}`;
  } else if (state.preset === 'official-sheet') {
    guaranteed = `90° context panorama, not for judging scale (LI TGN 06/19) · ${ILLUSTRATIVE_CAVEAT}`;
  } else {
    // 'explore' and 'custom' both land here: neither is a named scale
    // convention, so neither may borrow true-scale's or official-sheet's claim.
    guaranteed = ILLUSTRATIVE_CAVEAT;
  }
  return { text: `${guaranteed} · ${detail}`, guaranteed };
}
